/** A language the Code Image tool highlights: Prism's grammar key, the name on the page, the file extension in the title. */
export interface CodeLanguage {
  key: string;
  label: string;
  ext: string;
}

export const CODE_LANGUAGES: CodeLanguage[] = [
  { key: 'typescript', label: 'TypeScript', ext: 'ts' },
  { key: 'javascript', label: 'JavaScript', ext: 'js' },
  { key: 'tsx', label: 'TSX', ext: 'tsx' },
  { key: 'jsx', label: 'JSX', ext: 'jsx' },
  { key: 'python', label: 'Python', ext: 'py' },
  { key: 'java', label: 'Java', ext: 'java' },
  { key: 'kotlin', label: 'Kotlin', ext: 'kt' },
  { key: 'swift', label: 'Swift', ext: 'swift' },
  { key: 'go', label: 'Go', ext: 'go' },
  { key: 'rust', label: 'Rust', ext: 'rs' },
  { key: 'c', label: 'C', ext: 'c' },
  { key: 'cpp', label: 'C++', ext: 'cpp' },
  { key: 'csharp', label: 'C#', ext: 'cs' },
  { key: 'php', label: 'PHP', ext: 'php' },
  { key: 'ruby', label: 'Ruby', ext: 'rb' },
  { key: 'bash', label: 'Bash', ext: 'sh' },
  { key: 'markup', label: 'HTML', ext: 'html' },
  { key: 'css', label: 'CSS', ext: 'css' },
  { key: 'scss', label: 'SCSS', ext: 'scss' },
  { key: 'json', label: 'JSON', ext: 'json' },
  { key: 'yaml', label: 'YAML', ext: 'yml' },
  { key: 'sql', label: 'SQL', ext: 'sql' },
  { key: 'markdown', label: 'Markdown', ext: 'md' },
];

/** The snippet a language opens with. A language without one keeps the code already on the page. */
export const CODE_SAMPLES: { [ language: string ]: string } = {
  typescript: `interface Theme {
  name: string;
  dark: boolean;
}

// Every theme greets you in its own colors
export function greet(theme: Theme): string {
  const mode = theme.dark ? 'dark' : 'light';
  return \`Hello from \${theme.name}, a \${mode} theme\`;
}

console.log(greet({ name: 'Codigrate', dark: true }));`,
  javascript: `// Every theme greets you in its own colors
export function greet(theme) {
  const mode = theme.dark ? 'dark' : 'light';
  return \`Hello from \${theme.name}, a \${mode} theme\`;
}

const themes = ['Tokyo', 'Sakura', 'Etna'];
themes.forEach((name) => console.log(greet({ name, dark: true })));`,
  python: `from dataclasses import dataclass


@dataclass
class Theme:
    name: str
    dark: bool = True


def greet(theme: Theme) -> str:
    # Every theme greets you in its own colors
    mode = "dark" if theme.dark else "light"
    return f"Hello from {theme.name}, a {mode} theme"


print(greet(Theme("Codigrate")))`,
  java: `public record Theme(String name, boolean dark) {

    // Every theme greets you in its own colors
    public String greet() {
        String mode = dark ? "dark" : "light";
        return "Hello from %s, a %s theme".formatted(name, mode);
    }

    public static void main(String[] args) {
        System.out.println(new Theme("Codigrate", true).greet());
    }
}`,
  kotlin: `data class Theme(val name: String, val dark: Boolean = true)

// Every theme greets you in its own colors
fun Theme.greet(): String {
    val mode = if (dark) "dark" else "light"
    return "Hello from $name, a $mode theme"
}

fun main() {
    println(Theme("Codigrate").greet())
}`,
  swift: `struct Theme {
    let name: String
    var dark = true

    // Every theme greets you in its own colors
    func greet() -> String {
        let mode = dark ? "dark" : "light"
        return "Hello from \\(name), a \\(mode) theme"
    }
}

print(Theme(name: "Codigrate").greet())`,
  go: `package main

import "fmt"

type Theme struct {
	Name string
	Dark bool
}

// Every theme greets you in its own colors
func (t Theme) Greet() string {
	mode := "light"
	if t.Dark {
		mode = "dark"
	}
	return fmt.Sprintf("Hello from %s, a %s theme", t.Name, mode)
}

func main() {
	fmt.Println(Theme{"Codigrate", true}.Greet())
}`,
  rust: `struct Theme {
    name: String,
    dark: bool,
}

impl Theme {
    // Every theme greets you in its own colors
    fn greet(&self) -> String {
        let mode = if self.dark { "dark" } else { "light" };
        format!("Hello from {}, a {} theme", self.name, mode)
    }
}

fn main() {
    let theme = Theme { name: "Codigrate".into(), dark: true };
    println!("{}", theme.greet());
}`,
  markup: `<!-- Every theme greets you in its own colors -->
<section class="theme" data-dark="true">
  <h1>Hello from Codigrate</h1>
  <p>A <strong>dark</strong> theme for your editor.</p>
  <a href="https://codigrate.com/themes">Browse themes</a>
</section>`,
  css: `/* Every theme greets you in its own colors */
.theme {
  --accent: #00baff;
  color: var(--accent);
  background: linear-gradient(135deg, #142b37, #123243);
  border-radius: 12px;
}

.theme:hover {
  transform: translateY(-2px);
}`,
  json: `{
  "name": "Codigrate",
  "dark": true,
  "colors": {
    "accent": "#00BAFF",
    "background": "#142B37"
  },
  "editors": ["JetBrains", "VS Code"]
}`,
  sql: `-- Every theme and how often it was installed
SELECT t.name, COUNT(i.id) AS installs
FROM themes AS t
LEFT JOIN installs AS i ON i.theme_id = t.id
WHERE t.dark = TRUE
GROUP BY t.name
ORDER BY installs DESC
LIMIT 10;`,
  bash: `#!/usr/bin/env bash
# Every theme greets you in its own colors
name="\${1:-Codigrate}"

for mode in dark light; do
  echo "Hello from $name, a $mode theme"
done`,
};
